/* ===================================================================
   VendorLogo - REAL official vendor brand marks, inlined and bundled in
   the app JS (no hotlinked URL that can 404 or change). Each glyph is the
   vendor's actual official logo path from Simple Icons (simpleicons.org,
   24x24 single-path brand glyphs) rendered on a clean white tile in the
   vendor's official brand color. Google is the official 4-color mark.

   Logo source of truth (2026-06-28 rebuild, Brad):
   - Google      : official 4-color "G" (18x18 multi-path)
   - Square      : Simple Icons "Square"      (#000000)
   - QuickBooks  : Simple Icons "QuickBooks"  (#2CA01C)
   - Stripe      : Simple Icons "Stripe"      (#635BFF)
   - Zapier      : Simple Icons "Zapier"      (#FF4F00)
   - Calendly    : Simple Icons "Calendly"    (#006BFF)
   - Make        : Simple Icons "Make"        (#6D00CC)

   THREE partner connectors with no free glyph were sourced 2026-06-28 (Brad)
   DIRECTLY from each vendor's OWN official asset and inlined as REAL marks:
   - Jobber        : official icon mark (rounded-square "J"), #84EA00.
                     Source: inline <svg aria-label="Jobber"> in the
                     getjobber.com site header (live DOM, verified render).
   - Acuity        : official icon mark (interlocking-A), #1F2A44. Source:
                     acuityscheduling.com OWN brand-assets page ->
                     acuity-squarespace-logo.svg ("free to distribute");
                     icon isolated from the lockup.
   - Housecall Pro : official FULL LOCKUP (icon + "Housecall Pro" wordmark),
                     #002942, because the HCP icon glyph alone is not
                     recognizable. Source: housecallpro.com header SVG
                     (.../dist/images/icon-logo-header-resting.svg). Rendered
                     as a wide lockup tile, not a square icon tile.

   THREE webhook connectors added 2026-07-30 (Brad), each from the vendor's OWN
   official logo SVG (downloaded from their site), isolated + inlined as REAL marks:
   - ResponsiBid  : official app ICON isolated from the official wordmark lockup
                    (responsibid.com/.../Responsibid-Logo.svg) -- the cobalt
                    #0073FF rounded badge with the white monogram cut-out. The
                    navy #041844 wordmark was dropped; just the recognizable icon.
   - Sweep&Go     : official ICON isolated from the official wordmark lockup
                    (sweepandgo.com/.../sg-logo-vertical-color.svg) -- the two
                    swept cyan/teal strokes (#19E2E9 / #55CBD5) + the #3AB0CE
                    bar. The dark wordmark was dropped; just the icon.
   - Workiz       : brand-COLOR WORDMARK pill (honest fallback per the brief).
                    Workiz has no standalone symbol -- their official mark is a
                    yellow pill + "workiz" wordmark (workiz.com/.../workiz-logo.svg,
                    brand yellow #FFD400 / ink #23282B). No clean single glyph
                    exists to isolate, so we render the accurate brand-color
                    wordmark pill (yellow pill, dark ink) rather than trace a
                    fake glyph -- it mirrors the real Workiz lockup.

   Usage: <VendorLogo id="jobber" size={40} />
     - `id`   : one of the VendorId union members below.
     - `size` : tile edge length in px (default 40). The inner SVG is
                rendered at ~62% of the tile so it sits centered with
                padding.
     - `bare` : optional. Render just the SVG (no white tile). Only valid
                for glyph vendors (ignored for wordmark vendors).
   =================================================================== */
import type { ReactNode } from 'react';

export type VendorId =
  | 'google'
  | 'jobber'
  | 'housecall'
  | 'housecall_icon'
  | 'square'
  | 'quickbooks'
  | 'stripe'
  | 'zapier'
  | 'calendly'
  | 'acuity'
  | 'fub'
  | 'make'
  | 'workiz'
  | 'sweepandgo'
  | 'responsibid';

type VendorDef = {
  label: string;
  color: string;
  viewBox: string;
  multicolor?: boolean;
  body: ReactNode;
};

/* Brands rendered as an honest wordmark pill (brand name in brand color),
   because no clean official single-glyph SVG exists to isolate. `ink` is the
   text color (defaults to white); set it when the brand color is light and
   needs dark text (e.g. Workiz's yellow pill uses dark ink, matching their
   real lockup). */
type WordmarkDef = { label: string; color: string; ink?: string };
const WORDMARKS: Partial<Record<VendorId, WordmarkDef>> = {
  // Workiz: no standalone symbol exists -- their official mark is a yellow pill
  // + "workiz" wordmark. Honest brand-color pill: brand yellow #FFD400 with the
  // brand dark ink #23282B (white-on-yellow would be unreadable). Sourced from
  // workiz.com/wp-content/uploads/2022/06/workiz-logo.svg.
  workiz: { label: 'Workiz', color: '#FFD400', ink: '#23282B' },
};

/* A wide horizontal LOCKUP (icon + wordmark) rendered as one official SVG on
   a white tile - used when the vendor's icon glyph alone is not recognizable
   but we DO have their real official lockup asset. */
type LockupDef = { label: string; viewBox: string; aspect: number; body: ReactNode };
const LOCKUPS: Partial<Record<VendorId, LockupDef>> = {
  housecall: {
    label: 'Housecall Pro',
    viewBox: '0 0 170 26',
    aspect: 170 / 26,
    body: (
      <>
        <path fill="#002942" d="M0 5.3v15.363l7.49 4.033V.9L0 5.3ZM24.27 11.827h8.188v-8.36h2.864V22.46h-2.864v-7.92H24.27v7.956h-2.864V3.467h2.864v8.36Z" />
        <path fill="#002942" fillRule="evenodd" clipRule="evenodd" d="M37.819 16.41c0-1.723.624-3.19 1.872-4.4 1.248-1.21 2.754-1.833 4.516-1.833 1.8 0 3.305.623 4.553 1.833 1.249 1.21 1.836 2.713 1.836 4.51 0 1.797-.624 3.3-1.872 4.51-1.249 1.21-2.791 1.796-4.59 1.796-1.8 0-3.305-.623-4.516-1.833-1.212-1.21-1.8-2.75-1.8-4.583Zm2.827.073c0 1.21.33 2.127.954 2.823.661.697 1.506 1.064 2.607 1.064 1.102 0 1.946-.367 2.607-1.064.661-.696.955-1.613.955-2.786s-.33-2.09-.955-2.787c-.66-.696-1.505-1.063-2.607-1.063-1.065 0-1.909.367-2.57 1.063-.66.697-.991 1.614-.991 2.75Z" />
        <path fill="#002942" d="M55.847 10.507v6.856c0 1.98.77 2.97 2.35 2.97 1.578 0 2.35-.99 2.35-2.97v-6.856H63.3v6.93c0 .953-.11 1.796-.367 2.493-.22.623-.624 1.173-1.212 1.686-.917.807-2.13 1.21-3.561 1.21-1.432 0-2.607-.403-3.562-1.21-.587-.513-.991-1.063-1.211-1.686-.22-.55-.33-1.393-.33-2.493v-6.93h2.79ZM73.544 12.56l-2.276 1.21c-.367-.733-.808-1.1-1.359-1.1a.886.886 0 0 0-.66.257c-.184.183-.258.366-.258.66 0 .476.551.916 1.616 1.356 1.469.624 2.46 1.21 2.974 1.76.514.55.771 1.247.771 2.163 0 1.174-.44 2.127-1.285 2.897-.844.733-1.836 1.1-3.01 1.1-2.02 0-3.452-.99-4.26-2.933l2.35-1.1c.33.587.587.953.734 1.1.33.293.734.477 1.212.477.918 0 1.395-.44 1.395-1.284 0-.477-.367-.953-1.101-1.393a16.65 16.65 0 0 0-.845-.403c-.147-.074-.284-.138-.422-.202a16.986 16.986 0 0 1-.422-.202c-.808-.403-1.359-.77-1.69-1.173-.403-.513-.623-1.137-.623-1.943 0-1.027.367-1.907 1.064-2.567.735-.697 1.616-1.027 2.644-1.027 1.579-.036 2.717.77 3.451 2.347Z" />
        <path fill="#002942" fillRule="evenodd" clipRule="evenodd" d="M88.268 17.143h-8.592c.073.99.404 1.76.955 2.347.55.587 1.285.843 2.166.843.698 0 1.248-.146 1.726-.476.44-.33.954-.917 1.505-1.834l2.35 1.32a9.2 9.2 0 0 1-1.138 1.577c-.404.44-.845.806-1.285 1.1-.478.293-.955.476-1.506.623-.55.147-1.101.183-1.725.183-1.8 0-3.232-.586-4.296-1.723-1.102-1.173-1.616-2.677-1.616-4.583 0-1.907.514-3.41 1.579-4.583 1.065-1.174 2.46-1.724 4.222-1.724 1.763 0 3.158.55 4.186 1.687 1.028 1.1 1.542 2.677 1.542 4.62l-.073.623Zm-2.827-2.236c-.404-1.467-1.322-2.2-2.79-2.2-.331 0-.662.036-.956.146-.293.11-.55.257-.807.44-.257.184-.44.44-.625.697-.183.257-.293.587-.403.916h5.58ZM112.171 10.507h2.79v11.99h-2.79V21.25c-1.139 1.063-2.35 1.613-3.672 1.613-1.652 0-3.011-.587-4.112-1.796-1.065-1.21-1.616-2.75-1.616-4.584 0-1.796.551-3.3 1.616-4.473 1.064-1.21 2.423-1.797 4.038-1.797 1.396 0 2.644.587 3.746 1.724v-1.43Zm-6.573 5.976c0 1.137.294 2.09.918 2.787.624.733 1.432 1.1 2.387 1.1 1.028 0 1.872-.367 2.497-1.064.624-.733.954-1.65.954-2.786 0-1.137-.33-2.053-.954-2.787-.625-.696-1.469-1.063-2.46-1.063-.955 0-1.763.367-2.387 1.1-.661.733-.955 1.613-.955 2.713Z" />
        <path fill="#002942" d="M120.873 3.504v18.992h-2.754V3.504h2.754ZM126.821 3.504v18.992h-2.754V3.504h2.754Z" />
        <path fill="#002942" fillRule="evenodd" clipRule="evenodd" d="M138.13 14.797v7.7h-2.864V3.503h3.267c1.579 0 2.791.11 3.599.33a4.8 4.8 0 0 1 2.166 1.246c1.102 1.064 1.652 2.42 1.652 4.033 0 1.724-.587 3.117-1.762 4.144-1.175 1.026-2.754 1.54-4.7 1.54h-1.358Zm0-2.677h1.064c2.644 0 3.966-1.027 3.966-3.043 0-1.943-1.359-2.933-4.076-2.933h-.954v5.976Z" />
        <path fill="#002942" d="M151.127 10.507v1.063c.514-.55.955-.88 1.359-1.1.404-.22.881-.293 1.432-.293.734 0 1.505.22 2.276.696l-1.285 2.53c-.514-.366-1.028-.55-1.542-.55-1.505 0-2.276 1.137-2.276 3.41v6.197h-2.754V10.507h2.79Z" />
        <path fill="#002942" fillRule="evenodd" clipRule="evenodd" d="M157.002 16.41c0-1.723.624-3.19 1.873-4.4 1.248-1.21 2.754-1.833 4.516-1.833 1.799 0 3.304.623 4.553 1.833 1.248 1.21 1.836 2.713 1.836 4.51 0 1.797-.624 3.3-1.873 4.51-1.248 1.21-2.79 1.796-4.59 1.796-1.799 0-3.304-.623-4.516-1.833-1.175-1.21-1.799-2.75-1.799-4.583Zm2.827.073c0 1.21.331 2.127.955 2.823.661.697 1.505 1.064 2.607 1.064 1.101 0 1.946-.367 2.607-1.064.661-.696.954-1.613.954-2.786s-.33-2.09-.954-2.787c-.661-.696-1.506-1.063-2.607-1.063-1.065 0-1.909.367-2.57 1.063-.661.697-.992 1.614-.992 2.75Z" />
        <path fill="#002942" d="M97.227 12.67h.073s.037.037.037 0c.11 0 .257 0 .367.037a7.47 7.47 0 0 1 2.937.806V11.02c-1.064-.55-2.166-.843-3.34-.843-1.837 0-3.379.623-4.664 1.833-1.285 1.21-1.91 2.75-1.91 4.51 0 1.797.625 3.3 1.873 4.473 1.249 1.21 2.79 1.797 4.663 1.797 1.139 0 2.24-.294 3.378-.88v-2.494a7.43 7.43 0 0 1-2.937.844c-.183.037-.367.037-.55.037-1.029 0-1.91-.367-2.57-1.1-.662-.734-.992-1.65-.992-2.75s.33-2.017 1.028-2.75c.697-.734 1.542-1.027 2.607-1.027ZM9.657 22.46h3.157l.074-18.993H9.657v1.797h1.432L11.052 20.7H9.657v1.76Z" />
      </>
    ),
  },
};

/* Official brand path data per vendor (Simple Icons single-path glyphs). */
const VENDORS: Partial<Record<VendorId, VendorDef>> = {
  google: {
    label: 'Google',
    color: '#4285F4',
    viewBox: '0 0 18 18',
    multicolor: true,
    body: (
      <>
        <path
          fill="#4285F4"
          d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
        />
        <path
          fill="#34A853"
          d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.583-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
        />
        <path
          fill="#FBBC05"
          d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
        />
        <path
          fill="#EA4335"
          d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
        />
      </>
    ),
  },
  square: {
    label: 'Square',
    color: '#000000',
    viewBox: '0 0 24 24',
    body: (
      <path d="M4.01 0A4.01 4.01 0 000 4.01v15.98c0 2.21 1.8 4 4.01 4.01h15.98C22.2 24 24 22.2 24 19.99V4A4.01 4.01 0 0019.99 0H4zm1.62 4.36h12.74c.7 0 1.26.57 1.26 1.27v12.74c0 .7-.56 1.27-1.26 1.27H5.63c-.7 0-1.26-.57-1.26-1.27V5.63a1.27 1.27 0 011.26-1.27zm3.83 4.35a.73.73 0 00-.73.73v5.09c0 .4.32.72.72.72h5.1a.73.73 0 00.73-.72V9.44a.73.73 0 00-.73-.73h-5.1Z" />
    ),
  },
  quickbooks: {
    label: 'QuickBooks',
    color: '#2CA01C',
    viewBox: '0 0 24 24',
    body: (
      <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm.642 4.1335c.9554 0 1.7296.776 1.7296 1.7332v9.0667h1.6c1.614 0 2.9275-1.3156 2.9275-2.933 0-1.6173-1.3136-2.9333-2.9276-2.9333h-.6654V7.3334h.6654c2.5722 0 4.6577 2.0897 4.6577 4.667 0 2.5774-2.0855 4.6666-4.6577 4.6666H12.642zM7.9837 7.333h3.3291v12.533c-.9555 0-1.73-.7759-1.73-1.7332V9.0662H7.9837c-1.6146 0-2.9277 1.316-2.9277 2.9334 0 1.6175 1.3131 2.9333 2.9277 2.9333h.6654v1.7332h-.6654c-2.5725 0-4.6577-2.0892-4.6577-4.6665 0-2.5771 2.0852-4.6666 4.6577-4.6666Z" />
    ),
  },
  stripe: {
    label: 'Stripe',
    color: '#635BFF',
    viewBox: '0 0 24 24',
    body: (
      <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.594-7.305h.003z" />
    ),
  },
  zapier: {
    label: 'Zapier',
    color: '#FF4F00',
    viewBox: '0 0 24 24',
    body: (
      <path d="M4.157 0A4.151 4.151 0 0 0 0 4.161v15.678A4.151 4.151 0 0 0 4.157 24h15.682A4.152 4.152 0 0 0 24 19.839V4.161A4.152 4.152 0 0 0 19.839 0H4.157Zm10.61 8.761h.03a.577.577 0 0 1 .23.038.585.585 0 0 1 .201.124.63.63 0 0 1 .162.431.612.612 0 0 1-.162.435.58.58 0 0 1-.201.128.58.58 0 0 1-.23.042.529.529 0 0 1-.235-.042.585.585 0 0 1-.332-.328.559.559 0 0 1-.038-.235.613.613 0 0 1 .17-.431.59.59 0 0 1 .405-.162Zm2.853 1.572c.03.004.061.004.095.004.325-.011.646.064.937.219.238.144.431.355.552.609.128.279.189.582.185.888v.193a2 2 0 0 1 0 .219h-2.498c.003.227.075.45.204.642a.78.78 0 0 0 .646.265.714.714 0 0 0 .484-.136.642.642 0 0 0 .23-.318l.915.257a1.398 1.398 0 0 1-.28.537c-.14.159-.321.284-.521.355a2.234 2.234 0 0 1-.836.136 1.923 1.923 0 0 1-1.001-.245 1.618 1.618 0 0 1-.665-.703 2.221 2.221 0 0 1-.227-1.036 1.95 1.95 0 0 1 .48-1.398 1.9 1.9 0 0 1 1.3-.488Zm-9.607.023c.162.004.325.026.48.079.207.065.4.174.563.314.26.302.393.692.366 1.088v2.276H8.53l-.109-.711h-.065c-.064.163-.155.31-.272.439a1.122 1.122 0 0 1-.374.264 1.023 1.023 0 0 1-.453.083 1.334 1.334 0 0 1-.866-.264.965.965 0 0 1-.329-.801.993.993 0 0 1 .076-.431 1.02 1.02 0 0 1 .242-.363 1.478 1.478 0 0 1 1.043-.303h.952v-.181a.696.696 0 0 0-.136-.454.553.553 0 0 0-.438-.154.695.695 0 0 0-.378.086.48.48 0 0 0-.193.254l-.99-.144a1.26 1.26 0 0 1 .257-.563c.14-.174.321-.302.533-.378.261-.091.54-.136.82-.129.053-.003.106-.007.163-.007Zm4.384.007c.174 0 .347.038.506.114.182.083.34.211.458.374.257.423.377.911.351 1.406a2.53 2.53 0 0 1-.355 1.448 1.148 1.148 0 0 1-1.009.517c-.204 0-.401-.045-.582-.136a1.052 1.052 0 0 1-.48-.457 1.298 1.298 0 0 1-.114-.234h-.045l.004 1.784h-1.059v-4.713h.904l.117.805h.057c.068-.208.177-.401.328-.56a1.129 1.129 0 0 1 .843-.344h.076v-.004Zm7.559.084h.903l.113.805h.053a1.37 1.37 0 0 1 .235-.484.813.813 0 0 1 .313-.242.82.82 0 0 1 .39-.076h.234v1.051h-.401a.662.662 0 0 0-.313.008.623.623 0 0 0-.272.155.663.663 0 0 0-.174.26.683.683 0 0 0-.027.314v1.875h-1.054v-3.666Zm-17.515.003h3.262v.896L3.73 13.104l.034.113h1.973l.042.9H2.4v-.9l1.931-1.754-.045-.117H2.441v-.896Zm11.815 0h1.055v3.659h-1.055V10.45Zm3.443.684.019.016a.69.69 0 0 0-.351.045.756.756 0 0 0-.287.204c-.11.155-.174.336-.189.522h1.545c-.034-.526-.257-.787-.74-.787h.003Zm-5.718.163c-.026 0-.057 0-.083.004a.78.78 0 0 0-.31.053.746.746 0 0 0-.257.189 1.016 1.016 0 0 0-.204.695v.064c-.015.257.057.507.204.711a.634.634 0 0 0 .253.196.638.638 0 0 0 .314.061.644.644 0 0 0 .578-.265c.14-.223.204-.48.189-.74a1.216 1.216 0 0 0-.181-.711.677.677 0 0 0-.503-.257Zm-4.509 1.266a.464.464 0 0 0-.268.102.373.373 0 0 0-.114.276c0 .053.008.106.027.155a.375.375 0 0 0 .087.132.576.576 0 0 0 .397.11v.004a.863.863 0 0 0 .563-.182.573.573 0 0 0 .211-.457v-.14h-.903Z" />
    ),
  },
  calendly: {
    label: 'Calendly',
    color: '#006BFF',
    viewBox: '0 0 24 24',
    body: (
      <path d="M19.655 14.262c.281 0 .557.023.828.064 0 .005-.005.01-.005.014-.105.267-.234.534-.381.786l-1.219 2.106c-1.112 1.936-3.177 3.127-5.411 3.127h-2.432c-2.23 0-4.294-1.191-5.412-3.127l-1.218-2.106a6.251 6.251 0 0 1 0-6.252l1.218-2.106C6.736 4.832 8.8 3.641 11.035 3.641h2.432c2.23 0 4.294 1.191 5.411 3.127l1.219 2.106c.147.252.271.519.381.786 0 .004.005.009.005.014-.267.041-.543.064-.828.064-1.816 0-2.501-.607-3.291-1.306-.764-.676-1.711-1.517-3.44-1.517h-1.029c-1.251 0-2.387.455-3.2 1.278-.796.805-1.233 1.904-1.233 3.099v1.411c0 1.196.437 2.295 1.233 3.099.813.823 1.949 1.278 3.2 1.278h1.034c1.729 0 2.676-.841 3.439-1.517.791-.703 1.471-1.306 3.287-1.301Zm.005-3.237c.399 0 .794-.036 1.179-.11-.002-.004-.002-.01-.002-.014-.073-.414-.193-.823-.349-1.218.731-.12 1.407-.396 1.986-.819 0-.004-.005-.013-.005-.018-.331-1.085-.832-2.101-1.489-3.03-.649-.915-1.435-1.719-2.331-2.395-1.867-1.398-4.088-2.138-6.428-2.138-1.448 0-2.855.28-4.175.841-1.273.543-2.423 1.315-3.407 2.299S2.878 6.552 2.341 7.83c-.557 1.324-.842 2.726-.842 4.175 0 1.448.281 2.855.842 4.174.542 1.274 1.314 2.423 2.298 3.407s2.129 1.761 3.407 2.299c1.324.556 2.727.841 4.175.841 2.34 0 4.561-.74 6.428-2.137a10.815 10.815 0 0 0 2.331-2.396c.652-.929 1.158-1.949 1.489-3.03 0-.004.005-.014.005-.018-.579-.423-1.255-.699-1.986-.819.161-.395.276-.804.349-1.218.005-.009.005-.014.005-.023.869.166 1.692.506 2.404 1.035.685.505.552 1.075.446 1.416C22.184 20.437 17.619 24 12.221 24c-6.625 0-12-5.375-12-12s5.37-12 12-12c5.398 0 9.963 3.563 11.471 8.464.106.341.239.915-.446 1.421-.717.529-1.535.873-2.404 1.034.128.716.128 1.45 0 2.166-.387-.074-.782-.11-1.182-.11-4.184 0-3.968 2.823-6.736 2.823h-1.029c-1.899 0-3.15-1.357-3.15-3.095v-1.411c0-1.738 1.251-3.094 3.15-3.094h1.034c2.768 0 2.552 2.823 6.731 2.827Z" />
    ),
  },
  make: {
    label: 'Make',
    color: '#6D00CC',
    viewBox: '0 0 24 24',
    body: (
      <path d="M13.38 3.498c-.27 0-.511.19-.566.465L9.85 18.986a.578.578 0 0 0 .453.678l4.095.826a.58.58 0 0 0 .682-.455l2.963-15.021a.578.578 0 0 0-.453-.678l-4.096-.826a.589.589 0 0 0-.113-.012zm-5.876.098a.576.576 0 0 0-.516.318L.062 17.697a.575.575 0 0 0 .256.774l3.733 1.877a.578.578 0 0 0 .775-.258l6.926-13.781a.577.577 0 0 0-.256-.776L7.762 3.658a.571.571 0 0 0-.258-.062zm11.74.115a.576.576 0 0 0-.576.576v15.426c0 .318.258.578.576.578h4.178a.58.58 0 0 0 .578-.578V4.287a.578.578 0 0 0-.578-.576Z" />
    ),
  },
  // Housecall Pro official ICON mark (the house glyph), isolated from the
  // start of the official header lockup SVG (the two leading paths form the
  // little "house" icon). Brand navy #002942. Used as a SQUARE 36px tile so
  // the card matches Google/Jobber/Square/QuickBooks instead of rendering the
  // oversized full wordmark lockup inline. (2026-06-29, Brad.)
  housecall_icon: {
    label: 'Housecall Pro',
    color: '#002942',
    viewBox: '-1 0 16 26',
    body: (
      <>
        <path fill="#002942" d="M0 5.3v15.363l7.49 4.033V.9L0 5.3Z" />
        <path fill="#002942" d="M9.657 22.46h3.157l.074-18.993H9.657v1.797h1.432L11.052 20.7H9.657v1.76Z" />
      </>
    ),
  },
  // Jobber official icon mark (rounded-square "J"), from the getjobber.com
  // header inline <svg aria-label="Jobber">. Brand green #84EA00.
  jobber: {
    label: 'Jobber',
    color: '#84EA00',
    viewBox: '0 0 65.2 65.7',
    body: (
      <path d="M56.1.6c-.4-.4-.8-.6-1.3-.6H24.1C10.8 0 0 10.8 0 24.1v30.7c0 .5.2 1 .6 1.3l9 9c.4.4.8.6 1.3.6h30.7c13.3 0 24.1-10.8 24.1-24.1V11c0-.5-.2-1-.6-1.3zM8.9 24.1v32.1c0 .6-.7.8-1.1.4l-2.9-2.9s-.2-.3-.2-.4V24.1c0-10.7 8.7-19.4 19.4-19.4h27.5c.3 0 .6.3.6.6v36.3c0 5.9-4.8 10.6-10.6 10.6H23.2c-.4 0-.7-.4-.6-.9l1.1-2.9c0-.2.3-.4.6-.4h16.5c4.1 0 7.4-3.3 7.4-7.4V9.5c0-.3-.3-.6-.6-.6H24.1c-8.4-.1-15.2 6.8-15.2 15.2m13.4 18.8V25c0-1.5 1.2-2.8 2.8-2.8H43c.3 0 .6.3.6.6v17.9c0 1.5-1.2 2.8-2.8 2.8H22.9c-.3 0-.6-.3-.6-.6m38.8-1.3c0 10.7-8.7 19.4-19.4 19.4H14.1c-.3 0-.6-.3-.6-.6V24.1c0-5.9 4.8-10.6 10.6-10.6h18.5c.4 0 .7.4.6.9l-1.1 2.9c0 .2-.3.4-.6.4H25c-4.1 0-7.4 3.3-7.4 7.4v31.3c0 .3.3.6.6.6h23.4c8.4 0 15.3-6.8 15.3-15.3V9.5c0-.6.7-.8 1.1-.4l2.9 2.9s.2.3.2.4z" />
    ),
  },
  // Acuity Scheduling official icon mark (interlocking-A), isolated from the
  // official acuity-squarespace-logo.svg on acuityscheduling.com brand page.
  // Brand navy #1F2A44.
  acuity: {
    label: 'Acuity Scheduling',
    color: '#1F2A44',
    viewBox: '0 0 66 54',
    body: (
      <g fillRule="nonzero">
        <path d="M13.779 28.477L34.791 7.44a7.57 7.57 0 0110.695 0l1.635 1.637 3.276-3.28-1.64-1.638a12.201 12.201 0 00-17.246 0L10.504 25.201l3.275 3.276z" />
        <path d="M41.823 14.388l-3.271-3.28L17.54 32.146a7.577 7.577 0 01-5.44 2.543 7.566 7.566 0 01-5.58-2.219 7.587 7.587 0 01-2.215-5.587 7.596 7.596 0 012.54-5.448l18.128-18.16-3.28-3.262L3.565 18.168a12.225 12.225 0 00.09 17.185 12.189 12.189 0 008.563 3.577 12.189 12.189 0 008.598-3.49l21.007-21.052zM62.736 18.155a12.193 12.193 0 00-17.246 0L24.48 39.192l3.275 3.28 21.007-21.037a7.569 7.569 0 0110.39.306 7.594 7.594 0 01.305 10.405L41.324 50.3l3.275 3.276 18.128-18.15a12.241 12.241 0 000-17.276" />
        <path d="M52.527 25.103L31.515 46.141a7.57 7.57 0 01-10.695 0l-1.636-1.638-3.28 3.262 1.636 1.638a12.197 12.197 0 0017.25 0l21.008-21.037-3.271-3.263z" />
      </g>
    ),
  },
  // Follow Up Boss official icon mark: three stacked upward CHEVRONS (FUB is a
  // Zillow company; the chevron stack is their app/icon mark, distinct from the
  // "fub" wordmark). Recreated faithfully in FUB's three brand chevron colors --
  // gold top, blue middle, coral bottom -- as crisp inline geometry so it scales
  // cleanly in the 36px square tile alongside the other connector logos. Just
  // the MARK (no "by Zillow" wordmark) per Steve. (2026-06-29, Brad.) Nominative
  // use to identify the integration, same as every other connector logo.
  fub: {
    label: 'Follow Up Boss',
    color: '#0098D7',
    viewBox: '0 0 24 24',
    multicolor: true,
    // Each chevron: an upward "^" stroke drawn as a filled shape. Stacked top
    // (gold) -> middle (blue) -> bottom (coral), evenly spaced.
    body: (
      <>
        <path fill="#FFC533" d="M12 3.2 3.4 9.1l2.05 1.46L12 6.04l6.55 4.52L20.6 9.1 12 3.2Z" />
        <path fill="#0098D7" d="M12 9.06 3.4 14.96l2.05 1.46L12 11.9l6.55 4.52 2.05-1.46L12 9.06Z" />
        <path fill="#F0533F" d="M12 14.92 3.4 20.82l2.05 1.46L12 17.76l6.55 4.52 2.05-1.46L12 14.92Z" />
      </>
    ),
  },
  // ResponsiBid official app ICON, isolated from the official wordmark lockup
  // (responsibid.com/.../Responsibid-Logo.svg): the cobalt #0073FF rounded badge
  // with the white monogram cut-out. The navy #041844 "responsibid" wordmark
  // that follows it in the lockup is dropped -- just the recognizable icon.
  // (2026-07-30, Brad.)
  responsibid: {
    label: 'ResponsiBid',
    color: '#0073FF',
    viewBox: '0 0 46.5 30.55',
    multicolor: true,
    body: (
      <>
        <path fill="#0073FF" d="M46.48,15.27a58.52,58.52,0,0,0-.35-6.74,2.35,2.35,0,0,0-1.66-1.68Q35.07,3.15,24.9.22a6.41,6.41,0,0,0-3.31,0Q11.42,3.16,2,6.85c-1,.38-1.58,1-1.65,1.68A58.54,58.54,0,0,0,0,15.27,58.71,58.71,0,0,0,.36,22c.07.68.69,1.3,1.65,1.68q9.4,3.69,19.58,6.62a6.2,6.2,0,0,0,3.31,0Q35.06,27.4,44.47,23.7A2.35,2.35,0,0,0,46.13,22a58.7,58.7,0,0,0,.35-6.75" />
        <path fill="#fff" d="M22.66,20.76,19.9,16.67l-.19-.28.14-.15,2.59-2.64a2.8,2.8,0,0,0,.28-1.48c-.65-.93-1.45-2.07-2.07-2.94H8.36a.33.33,0,0,0-.3.19.36.36,0,0,0,0,.35c.47.65,1.29,1.82,1.74,2.48L8,21c0,.2.19.41.43.41H11.9c.24,0,.38-.16.43-.41l1.72-8.21c.08-.42.29-.76.73-.76h2.27a.84.84,0,0,1,.8.44.94.94,0,0,1-.12,1L15.2,16c-.24.25-.24.38,0,.78l3,4.41a.44.44,0,0,0,.37.19h3.7a.43.43,0,0,0,.37-.2.38.38,0,0,0,0-.41" />
        <path fill="#fff" d="M38.45,11.68V10.33a1.18,1.18,0,0,0-1.21-1.15H23.67a.31.31,0,0,0-.3.19.32.32,0,0,0,0,.35c.34.47.87,1.23,1.31,1.86v9.27a.53.53,0,0,0,.54.52h12a1.19,1.19,0,0,0,1.21-1.16V18.74a3.13,3.13,0,0,0-.28-1.68L36.4,15.27l1.77-1.78a3.43,3.43,0,0,0,.28-1.81m-6.26,4,1.6,1.53a.89.89,0,0,1,.13,1,.87.87,0,0,1-.81.43H29.82a.77.77,0,0,1-.8-.74V12.73a.77.77,0,0,1,.8-.74h3.29a.83.83,0,0,1,.67,1.41l-1.59,1.51a.53.53,0,0,0,0,.72" />
      </>
    ),
  },
  // Sweep&Go official ICON, isolated from the official wordmark lockup
  // (sweepandgo.com/.../sg-logo-vertical-color.svg): the two swept cyan/teal
  // strokes (#19E2E9 bright, #55CBD5 teal) plus the #3AB0CE bar. The dark
  // "sweep&go" wordmark that follows it is dropped -- just the icon.
  // (2026-07-30, Brad.)
  sweepandgo: {
    label: 'Sweep&Go',
    color: '#19E2E9',
    viewBox: '0 0 45.2 32.9',
    multicolor: true,
    body: (
      <>
        <path fill="#19E2E9" d="M18.449,2.49c-0.708,9.99-3.421,19.43-7.738,27.914H0c5.029-8.26,8.234-17.75,9.063-27.914H18.449L18.449,2.49z" />
        <path fill="#55CBD5" d="M31.811,2.49c-0.328,9.557-1.545,18.889-3.57,27.914h-9.609c2.162-9,3.465-18.336,3.814-27.914h5.469H31.811L31.811,2.49z" />
        <polygon fill="#3AB0CE" points="45.166,2.49 45.166,30.404 35.807,30.404 35.807,2.49 45.166,2.49" />
      </>
    ),
  },
};

export default function VendorLogo({
  id,
  size = 40,
  bare = false,
}: {
  id: VendorId;
  size?: number;
  bare?: boolean;
}) {
  const w = WORDMARKS[id];
  if (w) {
    // Honest wordmark pill (brand name in brand color).
    return (
      <span
        className="vlogo vlogo--wordmark"
        role="img"
        aria-label={`${w.label} logo`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: w.color,
          color: w.ink ?? '#fff',
          borderRadius: Math.round(size * 0.3),
          padding: `${Math.round(size * 0.13)}px ${Math.round(size * 0.24)}px`,
          fontFamily: "'DM Sans','Inter',sans-serif",
          fontWeight: 700,
          fontSize: Math.round(size * 0.32),
          lineHeight: 1,
          letterSpacing: '-.01em',
          flex: '0 0 auto',
          whiteSpace: 'nowrap',
        }}
      >
        {w.label}
      </span>
    );
  }

  const l = LOCKUPS[id];
  if (l) {
    // Official wide lockup (icon + wordmark) on a white tile.
    const glyphH = Math.round(size * 0.62);
    const glyphW = Math.round(glyphH * l.aspect);
    return (
      <span
        className="vlogo vlogo--lockup"
        role="img"
        aria-label={`${l.label} logo`}
        style={{
          height: size,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#fff',
          border: '1px solid rgba(15,23,42,.10)',
          borderRadius: Math.round(size * 0.28),
          padding: `0 ${Math.round(size * 0.34)}px`,
          flex: '0 0 auto',
        }}
      >
        <svg
          viewBox={l.viewBox}
          width={glyphW}
          height={glyphH}
          role="img"
          aria-label={`${l.label} logo`}
        >
          {l.body}
        </svg>
      </span>
    );
  }

  const v = VENDORS[id];
  if (!v) return null;
  const inner = Math.round(size * 0.62);
  const svg = (
    <svg
      viewBox={v.viewBox}
      width={inner}
      height={inner}
      role="img"
      aria-label={`${v.label} logo`}
      fill={v.multicolor ? undefined : v.color}
    >
      {v.body}
    </svg>
  );
  if (bare) return svg;
  return (
    <span
      className="vlogo"
      style={{
        width: size,
        height: size,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#fff',
        border: '1px solid rgba(15,23,42,.10)',
        borderRadius: Math.round(size * 0.28),
        flex: '0 0 auto',
      }}
    >
      {svg}
    </span>
  );
}
